import { Controller } from '@hotwired/stimulus';

/**
 * remark-lot — garde les deux LISTES de la fiche de remarque du CR (« Lots », « Concernés ») en phase
 * avec les deux sélecteurs multiples qui POSTENT (remarque multi-entreprises, étape 3.2, F26).
 *
 * QUI POSTE. Les deux `Form::SearchableSelectComponent` en `multiple: true, trigger: :external` :
 * `observation[lot_refs][]` et `observation[concerned_ids][]` (les intervenants AJOUTÉS À LA MAIN,
 * eux seuls), chacun avec un champ caché vide en tête. Les LIGNES de ce contrôleur ne postent rien :
 * elles sont la projection lisible des deux sélecteurs. Ce contrôleur ne lit et n'écrit les sélecteurs
 * QUE par l'API publique du contrôleur `searchable-select` (`values`, `setValues`, `setDisabled`,
 * `open`), jamais leur balisage ni leurs champs cachés. Le SERVEUR fait foi
 * (`Observations::AffectationArbitration`, puis `AffectationWriter` sous verrou) : rien ici ne bloque.
 *
 * LA RÈGLE (décisions 3 et 4, F5 ; `RemarkFormView#derived_by` côté serveur, la MÊME) :
 *   - les DÉRIVÉS sont les propriétaires (`owner_id`) des lots choisis, dans l'ordre de la liste,
 *     chacun rangé sous le PREMIER lot choisi qui le porte (« par le lot 09 ») ;
 *   - un intervenant AUSSI ajouté à la main reste MANUEL : l'origine `manual` l'emporte (F5), il reste
 *     posté et retirable, et retirer le lot ne le retire pas (E06). Décocher un manuel propriétaire
 *     d'un lot choisi le fait passer dérivé (verrouillé, plus posté) ;
 *   - un dérivé est COCHÉ et VERROUILLÉ dans le sélecteur des concernés (`setDisabled`), donc jamais
 *     posté (P13) ; retirer son dernier lot le fait disparaître (décocher puis déverrouiller) ;
 *   - « Toutes les entreprises » (value `all`) est exclusive : aucune ligne de concerné, sélecteur et
 *     « Ajouter » masqués, l'aide `allHelp`, le sélecteur des concernés VIDÉ (`setValues([])`) ;
 *   - l'ancien marché (`legacy` dans la carte `lots`) : le titulaire reste verrouillé « par le marché »
 *     (ses lignes du premier paint serveur sont gardées et ré-affichées), sélecteur et « Ajouter »
 *     masqués, les manuels affichés sans retrait (l'arbitre les ignore), l'aide `legacyHelp` ;
 *   - sinon l'aide `derivedHelp` (M12).
 *
 * L'ÉCRITURE DU SÉLECTEUR DES CONCERNÉS est SANS ÉTAT et SILENCIEUSE : tout déverrouiller, cocher
 * exactement manuels + dérivés (`setValues`, qui ne touche qu'aux lignes actives), puis verrouiller les
 * dérivés avec leur raison (`setDisabled`). Aucun `change` n'en sort : ce contrôleur n'a pas à
 * s'écouter lui-même, et le corps posté ne bouge que par un geste de l'utilisateur.
 *
 * AUCUNE CONNAISSANCE PROPRE. Noms, monogrammes, couleurs, raisons (« par le lot 09 »), explications,
 * libellés des boutons de retrait et phrases d'aide viennent des values servies par
 * `MeetingReports::RemarkFormComponent` ; les lignes ajoutées naissent de `<template>` rendus par le
 * MÊME partial que les lignes du premier paint (`remark_form/_lot_row`, `_concerned_row`), remplis par
 * `textContent` / `setAttribute` seulement, jamais `innerHTML` (#591 : un nom d'entreprise est une
 * donnée utilisateur). Les fonds d'avatar possibles viennent de la value `colors` : l'ancien est
 * RETIRÉ avant que le nouveau soit posé.
 *
 * MASQUAGE PAR `style.display`, jamais l'attribut `hidden` ni la classe `.hidden` (Tailwind v4 : un
 * utilitaire de display du nœud l'emporterait), et jamais `disabled` sur un sélecteur masqué : ses
 * champs cachés doivent partir dans le corps.
 *
 * PAS de `connect()`, PAS de `…ValueChanged()` : le premier paint (et le réaffichage 422) est celui du
 * serveur ; ce contrôleur ne réécrit l'écran qu'après un `change` d'un sélecteur.
 *
 * Targets:
 *   lotList / lotRow[data-value]       — la liste « Lots » et ses lignes
 *   lotsEmpty                          — « Aucun lot. »
 *   lotsAdder                          — le bouton « Ajouter » des lots (ouvreur du sélecteur)
 *   lotsPicker                         — l'enveloppe du sélecteur « Lots » (`change->remark-lot#lotsChanged`)
 *   lotRowTemplate                     — `<template>` d'une ligne de lot
 *   concernedList / concernedRow[data-actor-id][data-origin] — la liste « Concernés » et ses lignes
 *   concernedEmpty                     — « Aucun intervenant concerné. »
 *   concernedAdder                     — l'enveloppe du bouton « Ajouter » des concernés
 *   concernedPicker                    — l'enveloppe du sélecteur « Concernés » (`change->remark-lot#concernedChanged`)
 *   help                               — le `<p role="status">` d'aide (M12), toujours rendu
 *   lockedRowTemplate / manualRowTemplate — `<template>` d'une ligne verrouillée / ajoutée à la main
 *   Crochets dans une ligne : `[data-remark-lot-slot=icon|path|label|owner|remove|avatar|name|reason|explanation]`.
 *
 * Values:
 *   lots        — `{ ref => { label, path, owner, owner_id, reason, explanation, remove_label, all, legacy } }`
 *   actors      — `{ id ProjectActor => { name, initials, color, remove_label } }` (tout le carnet)
 *   colors      — les fonds d'avatar possibles (`Ui::AvatarComponent::BG_COLORS`)
 *   all         — la valeur sentinelle de « Toutes les entreprises »
 *   derivedHelp / allHelp / legacyHelp — les trois phrases d'aide
 *
 * Actions:
 *   openLots / openConcerned — sur le clic des boutons « Ajouter » : `open({ opener })` du sélecteur
 *   lotsChanged / concernedChanged — sur le `change` (qui bulle) du champ caché de tête d'un sélecteur
 *   removeLot / removeConcerned — sur le clic du bouton de retrait d'une ligne : retire la valeur du
 *                                 sélecteur (`setValues`, son `change` re-rend), focus sur « Ajouter »
 *
 * @example
 *   <div data-controller="remark-lot"
 *        data-remark-lot-lots-value='{"lot:9":{"label":"09 · Menuiseries","owner_id":"11","reason":"par le lot 09",…}}'
 *        data-remark-lot-actors-value='{"11":{"name":"Alu Concept","initials":"AC","color":"bg-teal-700",…}}'
 *        data-remark-lot-colors-value='["bg-primary-600","bg-teal-700"]'
 *        data-remark-lot-all-value="toutes"
 *        data-remark-lot-derived-help-value="L'intervenant d'un lot est concerné tant que la remarque porte ce lot."
 *        data-remark-lot-all-help-value="…" data-remark-lot-legacy-help-value="…">
 *     <button data-remark-lot-target="lotsAdder" data-action="remark-lot#openLots">Ajouter</button>
 *     <ul data-remark-lot-target="lotList">…</ul>
 *     <div data-remark-lot-target="lotsPicker" data-action="change->remark-lot#lotsChanged">
 *       <!-- Form::SearchableSelectComponent multiple, trigger: :external -->
 *     </div>
 *     <template data-remark-lot-target="lotRowTemplate">…</template>
 *     …
 *   </div>
 */

const SEARCHABLE_SELECT = 'searchable-select';
const ORIGIN_MANUAL = 'manual';
const ORIGIN_DERIVED = 'derived';
const ORIGIN_LEGACY = 'legacy';

export default class extends Controller {
  static targets = [
    'lotList',
    'lotRow',
    'lotsEmpty',
    'lotsAdder',
    'lotsPicker',
    'lotRowTemplate',
    'concernedList',
    'concernedRow',
    'concernedEmpty',
    'concernedAdder',
    'concernedPicker',
    'help',
    'lockedRowTemplate',
    'manualRowTemplate',
  ];

  static values = {
    lots: { type: Object, default: {} },
    actors: { type: Object, default: {} },
    colors: { type: Array, default: [] },
    all: { type: String, default: '' },
    derivedHelp: { type: String, default: '' },
    allHelp: { type: String, default: '' },
    legacyHelp: { type: String, default: '' },
  };

  // Les lignes « par le marché » du premier paint serveur : aucune value ne les décrit, elles sont
  // gardées à la première écriture pour revenir si l'utilisateur recoche l'ancien marché.
  #legacyRows = null;

  // ── Actions ────────────────────────────────────────────────────────────────────────────────────

  // « Ajouter » ouvre le sélecteur ; le bouton devient son ouvreur (aria-expanded, retour du focus).
  // Comme un bouton de divulgation, un second clic le referme (`finish` : le focus revient au bouton).
  openLots(event) {
    this.#toggle(this.lotsPickerTarget, event);
  }

  openConcerned(event) {
    this.#toggle(this.concernedPickerTarget, event);
  }

  // Le SEUL `change` qui compte est celui du champ caché de tête (le signal du sélecteur) ; la
  // recherche du sélecteur est arrêtée à sa racine, la garde reste en défense en profondeur.
  lotsChanged(event) {
    if (this.#fromPicker(event)) this.#render();
  }

  concernedChanged(event) {
    if (this.#fromPicker(event)) this.#render();
  }

  removeLot(event) {
    const row = event?.currentTarget?.closest?.('[data-remark-lot-target~="lotRow"]');
    const value = row?.dataset.value ?? '';
    const picker = this.#picker(this.lotsPickerTarget);
    if (!picker || value === '') return;

    picker.setValues(picker.values.filter((ref) => ref !== value));
    this.#focusButton(this.lotsAdderTarget);
  }

  removeConcerned(event) {
    const row = event?.currentTarget?.closest?.('[data-remark-lot-target~="concernedRow"]');
    const id = row?.dataset.actorId ?? '';
    const picker = this.#picker(this.concernedPickerTarget);
    if (!picker || id === '') return;

    picker.setValues(picker.values.filter((value) => value !== id));
    this.#focusButton(this.concernedAdderTarget);
  }

  #toggle(wrapper, event) {
    const picker = this.#picker(wrapper);
    if (!picker) return;
    if (picker.isOpen) {
      picker.finish();
    } else {
      picker.open({ opener: event?.currentTarget ?? null });
    }
  }

  // ── Rendu ──────────────────────────────────────────────────────────────────────────────────────

  #render() {
    const lots = this.#picker(this.lotsPickerTarget);
    if (!lots) return;

    const concerned = this.#picker(this.concernedPickerTarget);
    const refs = lots.values;
    this.#stashLegacyRows();
    this.#renderLots(refs);

    const manual = concerned ? concerned.values : [];
    if (this.allValue !== '' && refs.includes(this.allValue)) {
      this.#renderAll(concerned);
    } else if (refs.some((ref) => this.#lot(ref).legacy === true)) {
      this.#renderLegacy(concerned, manual);
    } else {
      this.#renderDerived(refs, concerned, manual);
    }
  }

  #renderLots(refs) {
    const existing = new Map(this.lotRowTargets.map((row) => [row.dataset.value ?? '', row]));
    const rows = refs.map((ref) => {
      const row = existing.get(ref) ?? this.#clone(this.lotRowTemplateTarget);
      this.#fillLotRow(row, ref, this.#lot(ref));
      return row;
    });
    this.lotListTarget.replaceChildren(...rows);
    this.lotsEmptyTarget.style.display = rows.length > 0 ? 'none' : '';
  }

  // « Toutes les entreprises » : aucun intervenant, sélecteur vidé et masqué.
  #renderAll(concerned) {
    this.#syncConcerned(concerned, [], new Map());
    this.concernedListTarget.replaceChildren();
    this.concernedEmptyTarget.style.display = 'none';
    this.#showPicker(false);
    this.#writeHelp(this.allHelpValue);
  }

  // L'ancien marché : son titulaire verrouillé « par le marché », les manuels sans retrait.
  #renderLegacy(concerned, manual) {
    const legacyRows = this.#legacyRows ?? [];
    const locks = new Map();
    legacyRows.forEach((row) => {
      const id = row.dataset.actorId ?? '';
      if (id === '' || locks.has(id)) return;
      locks.set(id, {
        reason: this.#slot(row, 'reason')?.textContent ?? '',
        explanation: this.#slot(row, 'explanation')?.textContent ?? '',
      });
    });
    const keptManual = manual.filter((id) => !locks.has(id));
    this.#syncConcerned(concerned, keptManual, locks);

    const rows = [
      ...legacyRows,
      ...keptManual.map((id) => this.#manualRow(id, { removable: false })),
    ];
    this.#showConcernedRows(rows);
    this.#showPicker(false);
    this.#writeHelp(this.legacyHelpValue);
  }

  // Des lots du planning (ou aucun) : les dérivés verrouillés, puis les manuels retirables.
  #renderDerived(refs, concerned, manual) {
    const manualIds = new Set(manual);
    const derived = new Map();
    refs.forEach((ref) => {
      const lot = this.#lot(ref);
      const owner = lot.owner_id === null || lot.owner_id === undefined ? '' : String(lot.owner_id);
      // F5 : un manuel propriétaire d'un lot reste manuel ; le PREMIER lot choisi donne la raison.
      if (owner === '' || manualIds.has(owner) || derived.has(owner)) return;
      derived.set(owner, lot);
    });

    const locks = new Map(
      [...derived].map(([id, lot]) => [
        id,
        { reason: lot.reason ?? '', explanation: lot.explanation ?? '' },
      ])
    );
    this.#syncConcerned(concerned, manual, locks);

    const rows = [
      ...[...derived].map(([id, lot]) => this.#derivedRow(id, lot)),
      ...manual.map((id) => this.#manualRow(id, { removable: true })),
    ];
    this.#showConcernedRows(rows);
    this.#showPicker(true);
    this.#writeHelp(this.derivedHelpValue);
  }

  // Le sélecteur des concernés, SANS ÉTAT et SILENCIEUX (voir l'en-tête) : coché = manuels + verrouillés,
  // posté = manuels. Seuls les intervenants du carnet (`actors`, la même source que ses options) y sont.
  #syncConcerned(concerned, manual, locks) {
    if (!concerned) return;

    const listed = (id) => Object.hasOwn(this.actorsValue, id);
    const everyone = Object.keys(this.actorsValue);
    const lockedIds = [...locks.keys()].filter(listed);
    concerned.setDisabled(Object.fromEntries(everyone.map((id) => [id, null])), { silent: true });
    concerned.setValues([...manual.filter(listed), ...lockedIds], { silent: true });
    concerned.setDisabled(Object.fromEntries(lockedIds.map((id) => [id, locks.get(id)])), {
      silent: true,
    });
  }

  #showConcernedRows(rows) {
    this.concernedListTarget.replaceChildren(...rows);
    this.concernedEmptyTarget.style.display = rows.length > 0 ? 'none' : '';
  }

  // Le sélecteur ET son « Ajouter » : offerts seulement quand un intervenant peut être choisi.
  #showPicker(visible) {
    const display = visible ? '' : 'none';
    this.concernedPickerTarget.style.display = display;
    if (this.hasConcernedAdderTarget) this.concernedAdderTarget.style.display = display;
  }

  // Une région live n'annonce que ce qui CHANGE : le texte n'est réécrit que s'il diffère.
  #writeHelp(text) {
    if (this.helpTarget.textContent !== text) this.helpTarget.textContent = text;
  }

  // ── Lignes ─────────────────────────────────────────────────────────────────────────────────────

  #fillLotRow(row, ref, lot) {
    row.setAttribute('data-value', ref);
    const icon = this.#slot(row, 'icon');
    if (icon) icon.style.display = lot.all === true ? '' : 'none';
    this.#writeText(row, 'path', lot.path, { title: true });
    this.#writeText(row, 'label', lot.label);
    this.#writeText(row, 'owner', lot.owner);
    this.#writeLabel(this.#slot(row, 'remove'), lot.remove_label);
  }

  #derivedRow(id, lot) {
    const actor = this.actorsValue[id] ?? {};
    const row = this.#reuseOrClone(ORIGIN_DERIVED, id, this.lockedRowTemplateTarget);
    this.#fillIdentity(row, id, ORIGIN_DERIVED, {
      name: actor.name ?? lot.owner ?? '',
      initials: actor.initials,
      color: actor.color,
    });
    this.#writeText(row, 'reason', lot.reason);
    this.#writeText(row, 'explanation', lot.explanation);
    return row;
  }

  // Une ligne manuelle rendue SANS bouton de retrait (premier paint sous l'ancien marché) n'est pas
  // réutilisée : hors du marché, le manuel doit redevenir retirable. Elle cède la place à une copie du
  // `<template>`, qui porte le bouton (masqué tant que le marché est porté).
  #manualRow(id, { removable }) {
    const actor = this.actorsValue[id] ?? {};
    const reusable = (row) => this.#slot(row, 'remove') !== null;
    const row = this.#reuseOrClone(ORIGIN_MANUAL, id, this.manualRowTemplateTarget, reusable);
    this.#fillIdentity(row, id, ORIGIN_MANUAL, actor);
    const remove = this.#slot(row, 'remove');
    if (remove) {
      this.#writeLabel(remove, actor.remove_label);
      remove.style.display = removable ? '' : 'none';
    }
    return row;
  }

  // Une ligne déjà à l'écran pour CE couple (origine, intervenant) est gardée (le premier paint du
  // serveur, le focus éventuel) ; sinon une copie du `<template>`.
  #reuseOrClone(origin, id, template, reusable = () => true) {
    const existing = this.concernedRowTargets.find(
      (row) => row.dataset.origin === origin && row.dataset.actorId === id && reusable(row)
    );
    return existing ?? this.#clone(template);
  }

  #fillIdentity(row, id, origin, { name, initials, color }) {
    row.setAttribute('data-actor-id', id);
    row.setAttribute('data-origin', origin);
    this.#writeText(row, 'name', name, { title: true });
    const avatar = this.#slot(row, 'avatar');
    if (!avatar) return;

    avatar.textContent = initials ?? '';
    avatar.setAttribute('title', name ?? '');
    this.colorsValue.forEach((bg) => avatar.classList.remove(bg));
    if (color) avatar.classList.add(color);
  }

  // ── Outils ─────────────────────────────────────────────────────────────────────────────────────

  #lot(ref) {
    return Object.hasOwn(this.lotsValue, ref) ? (this.lotsValue[ref] ?? {}) : {};
  }

  #fromPicker(event) {
    return event?.target?.type === 'hidden';
  }

  // Le contrôleur `searchable-select` DANS l'enveloppe, jamais ailleurs dans le document.
  #picker(wrapper) {
    const root = wrapper?.querySelector?.(`[data-controller~="${SEARCHABLE_SELECT}"]`);
    if (!root) return null;

    return this.application.getControllerForElementAndIdentifier(root, SEARCHABLE_SELECT);
  }

  #stashLegacyRows() {
    if (this.#legacyRows !== null) return;

    this.#legacyRows = this.concernedRowTargets.filter(
      (row) => row.dataset.origin === ORIGIN_LEGACY
    );
  }

  #clone(template) {
    return template.content.firstElementChild.cloneNode(true);
  }

  #slot(row, name) {
    return row.querySelector(`[data-remark-lot-slot="${name}"]`);
  }

  #writeText(row, name, text, { title = false } = {}) {
    const node = this.#slot(row, name);
    if (!node) return;

    node.textContent = text ?? '';
    if (title) node.setAttribute('title', text ?? '');
  }

  // Le nom accessible d'un bouton icône : `aria-label` ET `title` (M08), jamais son contenu.
  #writeLabel(button, label) {
    if (!button) return;

    button.setAttribute('aria-label', label ?? '');
    button.setAttribute('title', label ?? '');
  }

  #focusButton(holder) {
    const button = holder?.matches?.('button') ? holder : holder?.querySelector?.('button');
    button?.focus();
  }
}
